import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactECharts from 'echarts-for-react';
import './BarChart.scss';
import { EChartsOption, SeriesOption } from 'echarts';
import { normalizeKeyword, SELECTED_BRAND_COLOR } from '../../../utils/utils';
import { getChartThemeColors, getBarsPalette } from '../../../utils/chartTheme';
import { useTheme } from '../../../contexts/theme/ThemeContext';
import { Skeleton } from 'primereact/skeleton';
import { TagsResponse } from '../../../models/tags/TagsResponse';
import { KeywordResponse } from '../../../models/monitor/KeywordResponse';
import { findTagIdInKeyword } from '../../../helpers/keyword/KeywordHelper';
import { getTagLabel } from '../../../helpers/tags/TagsHelper';

interface BarChartProps {
  data?: string[][] | null;
  colorPalette?: string[];
  colorsByName?: { name: string; color: string }[];
  keywordsMapper?: Map<string, string>;
  tags?: TagsResponse[];
  commonKeywords?: KeywordResponse[];
  onLegendClick?: (legendStateMap: Map<string, boolean>) => void;
  chartSize?: string | null;
  selectedBrand?: string;
}

const BarChart: React.FC<BarChartProps> = ({
  data,
  colorPalette = getBarsPalette(),
  colorsByName,
  keywordsMapper,
  tags,
  commonKeywords,
  onLegendClick,
  chartSize,
  selectedBrand,
}) => {
  const [chartWidth, setChartWidth] = useState(0);
  const eChartsRef = useRef<ReactECharts>(null);
  const { theme } = useTheme();
  const [legendStateMap, setLegendStateMap] = useState<Map<string, boolean>>(new Map<string, boolean>());

  useEffect(() => {
    if (onLegendClick) {
      onLegendClick(legendStateMap);
    }
  }, [legendStateMap, onLegendClick]);

  const extendedColorsByName = useMemo(() => {
    if (!data || !data[0]) {
      return colorsByName || [];
    }

    const keywords = data[0].slice(1);
    const base = [...(colorsByName || [])];

    keywords.forEach((keyword, index) => {
      const normalizedKeyword = keyword.trim().toLowerCase();
      const exists = base.some((c) => normalizeKeyword(c.name).toLowerCase() === normalizedKeyword);

      if (!exists) {
        base.push({
          name: keyword,
          color: colorPalette[index % colorPalette.length],
        });
      }
    });

    return base;
  }, [data, colorsByName]);

  const seriesMeta = useMemo(() => {
    const keywords = data?.[0]?.slice(1) || [];
    return keywords.map((keyword, index) => {
      const tagId = commonKeywords ? findTagIdInKeyword(keyword, commonKeywords) : null;
      const keywordFromTags = tags ? getTagLabel(tags, tagId, keyword) : keyword;
      const displayLabel = keywordsMapper?.get(keywordFromTags) || keywordFromTags;
      const mappedByDisplay = extendedColorsByName.find(
        (c) => normalizeKeyword(c.name).toLowerCase() === displayLabel.trim().toLowerCase()
      )?.color;
      const mappedByOriginal = extendedColorsByName.find(
        (c) => normalizeKeyword(c.name).toLowerCase() === keyword.trim().toLowerCase()
      )?.color;
      const color = mappedByDisplay || mappedByOriginal || colorPalette[index % colorPalette.length];
      return { original: keyword, displayLabel, color } as {
        original: string;
        displayLabel: string;
        color: string;
      };
    });
  }, [data, tags, commonKeywords, keywordsMapper, extendedColorsByName]);

  useEffect(() => {
    if (seriesMeta.length > 0) {
      setLegendStateMap(new Map<string, boolean>(seriesMeta.map((m) => [m.displayLabel, true])));
    }
  }, [seriesMeta]);

  const chartPalette = useMemo(() => {
    if (seriesMeta.length === 0) return colorPalette;

    const basePalette = seriesMeta.map((m) => m.color);

    if (selectedBrand) {
      const index = seriesMeta.findIndex(
        (m) =>
          m.displayLabel.trim().toLowerCase() === selectedBrand.trim().toLowerCase() ||
          m.original.trim().toLowerCase() === selectedBrand.trim().toLowerCase()
      );

      if (index !== -1) {
        const updatedPalette = [...basePalette];
        updatedPalette[index] = SELECTED_BRAND_COLOR;
        return updatedPalette;
      }
    }

    return basePalette;
  }, [seriesMeta, selectedBrand]);

  const getSeries = (): SeriesOption[] => {
    const isToSmall = chartWidth < 700;

    return (
      seriesMeta.map((m) => ({
        name: m.displayLabel,
        type: 'bar',
        barWidth: isToSmall ? 12 : 15,
        barGap: isToSmall ? '50%' : '30%',
        itemStyle: {
          borderRadius: [2, 2, 0, 0],
        },
        label: {
          fontFamily: 'Inter,sans-serif',
          show: false,
          position: 'top',
          color: chartTheme.tooltipText,
          fontSize: isToSmall ? 8 : 10,
          fontWeight: 'bold',
        },
        emphasis: {
          label: {
            show: true,
          },
        },
      })) || []
    );
  };

  const yAxisMax = useMemo(() => {
    if (!data || data.length <= 1) {
      return 45;
    }

    const allValues: number[] = [];

    for (let i = 1; i < data.length; i++) {
      for (let j = 1; j < data[i].length; j++) {
        const value = parseFloat(data[i][j]);
        if (!isNaN(value)) {
          allValues.push(value);
        }
      }
    }

    if (allValues.length === 0) return 45;

    const maxValue = Math.max(...allValues);

    return maxValue + 5 < 45 ? maxValue + 5 : 45;
  }, [data]);

  const chartTheme = getChartThemeColors();

  const eChartsOptions: EChartsOption = {
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'none' },
      appendToBody: true,
      confine: false,
      borderColor: chartTheme.tooltipBorder,
      backgroundColor: chartTheme.tooltipBg,
      textStyle: {
        color: chartTheme.tooltipText,
      },
    },
    legend: {
      show: false,
      selected: Object.fromEntries(
        seriesMeta.map((m) => [m.displayLabel, legendStateMap.get(m.displayLabel) !== false])
      ),
    },
    grid: {
      top: '50px',
      bottom: 40,
      left: '0',
      right: '0',
    },
    xAxis: {
      type: 'category',
      axisLabel: {
        fontFamily: 'Inter,sans-serif',
        fontSize: chartWidth < 700 ? '10px' : '12px',
        fontWeight: 'normal',
        color: chartTheme.axisLabel,
        margin: 26,
      },
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
    },
    yAxis: {
      type: 'value',
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: true, lineStyle: { color: chartTheme.splitLine } },
      axisLabel: { color: chartTheme.axisLabel },
      max: yAxisMax,
    },
    dataset: {
      source: data || [],
    },
    color: chartPalette,
    series: getSeries(),
  };

  useEffect(() => {
    if (!eChartsRef.current) {
      return;
    }

    const chartInstance = eChartsRef.current.getEchartsInstance();
    const container = chartInstance.getDom();

    let resizeTimeout: NodeJS.Timeout | null = null;

    const resizeObserver = new ResizeObserver(() => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }

      resizeTimeout = setTimeout(() => {
        requestAnimationFrame(() => {
          if (!eChartsRef.current) return;

          const instance = eChartsRef.current.getEchartsInstance();
          if (!instance || instance.isDisposed()) return;

          const currentContainer = instance.getDom();
          if (!currentContainer) return;

          const newWidth = currentContainer.clientWidth;
          if (newWidth !== chartWidth && newWidth > 0) {
            setChartWidth(newWidth);
            try {
              instance.dispatchAction({
                type: 'hideTip',
              });

              instance.resize();
              instance.setOption(eChartsOptions, true);
            } catch (e) {
              console.warn('ECharts resize error:', e);
            }
          }
        });
      }, 100);
    });

    resizeObserver.observe(container);

    return () => {
      if (resizeTimeout) {
        clearTimeout(resizeTimeout);
      }
      resizeObserver.disconnect();
    };
  }, [eChartsOptions, chartWidth]);

  const chartSizeStyle = {
    height: '235px',
    width: '100%',
  };

  const handleLegendItemClick = (label: string) => {
    setLegendStateMap((prevState) => {
      const updatedMap = new Map(prevState);
      const currentVisibility = updatedMap.has(label) ? !updatedMap.get(label) : false;
      updatedMap.set(label, currentVisibility);

      const canManage =
        Array.from(updatedMap.values()).filter((value) => !value).length + 1 <= updatedMap.size;

      if (!canManage && prevState.get(label) === true) {
        return prevState;
      }

      if (eChartsRef.current) {
        const instance = eChartsRef.current.getEchartsInstance();
        instance.dispatchAction({
          type: 'legendToggleSelect',
          name: label,
        });
      }

      return updatedMap;
    });
  };

  if (!data) {
    return (
      <div className="bar-chart">
        <div className="bar-chart__legend">
          <Skeleton width="200px" height="20px" />
        </div>
        <div className="bar-chart__box">
          <Skeleton width="100%" height="300px" />
        </div>
      </div>
    );
  }

  const renderLegendItem = (index: number, meta: { displayLabel: string; color: string }) => {
    const isSelected = legendStateMap.get(meta.displayLabel);

    return (
      <div
        key={index}
        className={`bar-chart__legend-item ${isSelected ? 'bar-chart__legend-item--active' : ''}`}
        onClick={() => handleLegendItemClick(meta.displayLabel)}
      >
        <span>
          <span className="bar-chart__legend-color" style={{ background: meta.color }} />
          {meta.displayLabel}
        </span>
      </div>
    );
  };
  return (
    <div className={`bar-chart ${chartSize ? 'bar-chart--sm' : ''}`}>
      <div className="bar-chart__legend">{seriesMeta.map((m, index) => renderLegendItem(index, m))}</div>
      <div className="bar-chart__box">
        <ReactECharts key={theme} ref={eChartsRef} option={eChartsOptions} notMerge={true} style={chartSizeStyle} />
      </div>
    </div>
  );
};

export default BarChart;
